/**
 * Local Folder Source Registration Form
 * Handles folder selection, file extension filtering, and file tree display
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Badge } from '../UI/Badge';
import { registerLocalFolderSource } from '../../api/sources';
import { uploadArtifact } from '../../api/artifacts';
import { getProject } from '../../api/projects';
import { 
  Folder,
  File,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  Eye,
  EyeOff,
  Clock,
  CheckCircle,
  X,
} from 'lucide-react';
import { FileTreeNode } from './FileTreeNode';
import { ConfirmRegistrationModal } from './ConfirmRegistrationModal';
import { ClientUploadPromptModal } from './ClientUploadPromptModal';

interface LocalFolderFormProps {
  projectId: string;
  onSuccess: () => void;
  onClose: () => void;
}

export function LocalFolderForm({ projectId, onSuccess, onClose }: LocalFolderFormProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [folderName, setFolderName] = useState('');
  const [folderPath, setFolderPath] = useState('');
  const [folderDescription, setFolderDescription] = useState('');
  const [watchForChanges, setWatchForChanges] = useState(true);
  const [recursive, setRecursive] = useState(true);
  const [selectedExtensions, setSelectedExtensions] = useState<string[]>([]);
  const [scanningFolder, setScanningFolder] = useState(false);
  const [extensionStats, setExtensionStats] = useState<Record<string, { count: number; totalSize: number }>>({});
  const [fileTree, setFileTree] = useState<any[]>([]);
  const [hiddenPaths, setHiddenPaths] = useState<Set<string>>(new Set(['.git', 'node_modules', '.DS_Store']));
  const [showFileTree, setShowFileTree] = useState(false);
  const [expandedTreePaths, setExpandedTreePaths] = useState<Set<string>>(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalTab, setConfirmModalTab] = useState<'list' | 'tree'>('list');
  const [fileMetadata, setFileMetadata] = useState<Record<string, { rows?: number; columns?: number }>>({});
  const [directoryHandle, setDirectoryHandle] = useState<FileSystemDirectoryHandle | null>(null);
  const [modalExpandedPaths, setModalExpandedPaths] = useState<Set<string>>(new Set());
  const [isNameAutoGenerated, setIsNameAutoGenerated] = useState(true);
  
  // SSH/Remote connection state
  const [connectionType, setConnectionType] = useState<'local' | 'ssh' | 'unc'>('local');
  const [sshHost, setSshHost] = useState('');
  const [sshPort, setSshPort] = useState('22');
  const [sshUser, setSshUser] = useState('');
  const [sshPassword, setSshPassword] = useState('');
  const [sshKeyPath, setSshKeyPath] = useState('');
  const [sshRemotePath, setSshRemotePath] = useState('');
  const [testingConnection, setTestingConnection] = useState(false);
  
  // Project and tenant info
  const [projectTenantId, setProjectTenantId] = useState<string | null>(null);
  const [isKeystoneTenant, setIsKeystoneTenant] = useState(false);
  const KEYSTONE_TENANT_ID = '76cbf4c8-b48e-401b-bcbe-c7247110c7b7';
  
  // Check if Keystone tenant on mount
  useEffect(() => {
    const checkKeystoneTenant = async () => {
      try {
        const project = await getProject(projectId);
        setProjectTenantId(project.tenant_id);
        setIsKeystoneTenant(project.tenant_id === KEYSTONE_TENANT_ID);
      } catch (error) {
        console.error('Error fetching project:', error);
        // Default to false if we can't determine
        setIsKeystoneTenant(false);
      }
    };
    checkKeystoneTenant();
  }, [projectId]);
  
  // Upload prompt modal state
  const [showClientUploadPrompt, setShowClientUploadPrompt] = useState(false);
  const [uploadSourceId, setUploadSourceId] = useState<string | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadedCount, setUploadedCount] = useState(0);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  
  // File extension groups (same as in SourceList)
  const fileExtensionGroups = {
    deterministic: {
      label: 'Deterministic (Perfect Parsing)',
      extensions: ['.csv', '.xlsx', '.xls', '.xlsm'],
      description: 'No data loss - perfect parsing',
    },
    chunking: {
      label: 'Text Extraction (Chunking)',
      extensions: ['.pdf', '.docx', '.doc', '.pptx', '.ppt'],
      description: 'Text extraction with chunking for large files',
    },
    ocr: {
      label: 'OCR (Image Text Extraction)',
      extensions: ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.tif'],
      description: 'Text extraction via OCR - may have errors',
    },
    cad: {
      label: 'CAD Files',
      extensions: ['.dwg', '.dxf'],
      description: 'Computer-aided design drawings',
    },
    other: {
      label: 'Other',
      extensions: [],
      description: 'Unsupported file types',
    },
  };
  
  const supportedExtensions = new Set(
    Object.values(fileExtensionGroups)
      .filter(group => group !== fileExtensionGroups.other)
      .flatMap(group => group.extensions)
  );
  
  const extensionDescriptions: Record<string, string> = {
    '.pdf': 'PDF Document',
    '.docx': 'Word Document (2007+)',
    '.doc': 'Word Document (Legacy)',
    '.xlsx': 'Excel Workbook (2007+)',
    '.xls': 'Excel Workbook (Legacy)',
    '.xlsm': 'Excel Macro-Enabled Workbook',
    '.csv': 'Comma-Separated Values',
    '.pptx': 'PowerPoint Presentation',
    '.ppt': 'PowerPoint (Legacy)',
    '.jpg': 'JPEG Image (OCR)',
    '.jpeg': 'JPEG Image (OCR)',
    '.png': 'PNG Image (OCR)',
    '.gif': 'GIF Image',
    '.bmp': 'Bitmap Image (OCR)',
    '.tiff': 'TIFF Image (OCR)',
    '.tif': 'TIFF Image (OCR)',
    '.dwg': 'AutoCAD Drawing',
    '.dxf': 'Drawing Exchange Format',
  };
  
  // Calculate file count for auto-generated name
  const fileCountForName = useMemo(() => {
    if (fileTree.length === 0 || selectedExtensions.length === 0) {
      return 0;
    }
    
    let count = 0;
    const countFiles = (node: any) => {
      if (node.type === 'file') {
        const ext = node.extension || '(no extension)';
        if (selectedExtensions.includes(ext) && supportedExtensions.has(ext)) {
          count++;
        }
      } else if (node.children) {
        node.children.forEach(countFiles);
      }
    };
    
    fileTree.forEach(countFiles);
    return count;
  }, [fileTree, selectedExtensions, supportedExtensions]);
  
  // Auto-generate source name
  const autoGeneratedName = useMemo(() => {
    if (!folderPath || selectedExtensions.length === 0) {
      return folderPath || '';
    }
    
    const fileCount = fileCountForName;
    const folderNamePart = folderPath.split(/[/\\]/).filter(Boolean).pop() || folderPath;
    
    const extensionNames: Record<string, string> = {
      '.pdf': 'PDF',
      '.docx': 'Word',
      '.doc': 'Word',
      '.xlsx': 'Excel',
      '.xls': 'Excel',
      '.xlsm': 'Excel',
      '.csv': 'CSV',
      '.pptx': 'PowerPoint',
      '.ppt': 'PowerPoint',
      '.jpg': 'Images',
      '.jpeg': 'Images',
      '.png': 'Images',
      '.gif': 'Images',
      '.bmp': 'Images',
      '.tiff': 'Images',
      '.tif': 'Images',
      '.dwg': 'CAD',
      '.dxf': 'CAD',
    };
    
    const typeGroups: Record<string, string[]> = {};
    selectedExtensions.forEach(ext => {
      const typeName = extensionNames[ext] || ext.toUpperCase().replace('.', '');
      if (!typeGroups[typeName]) {
        typeGroups[typeName] = [];
      }
      typeGroups[typeName].push(ext);
    });
    
    const typeNames = Object.keys(typeGroups);
    let typeDescription = '';
    if (typeNames.length === 1) {
      typeDescription = typeNames[0] + ' Files';
    } else if (typeNames.length <= 3) {
      typeDescription = typeNames.join(', ') + ' Files';
    } else {
      typeDescription = `${typeNames.length} File Types`;
    }
    
    const nameParts = [folderNamePart];
    if (typeDescription) {
      nameParts.push(typeDescription);
    }
    if (fileCount > 0) {
      nameParts.push(`(${fileCount} file${fileCount !== 1 ? 's' : ''})`);
    }
    
    return nameParts.join(' - ');
  }, [folderPath, selectedExtensions, fileCountForName]);
  
  // Auto-update folderName when auto-generated name changes
  useEffect(() => {
    if (isNameAutoGenerated && autoGeneratedName) {
      setFolderName(autoGeneratedName);
    }
  }, [autoGeneratedName, isNameAutoGenerated]);
  
  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }, []);
  
  const shouldIgnore = useCallback((path: string, name: string): boolean => {
    const pathLower = path.toLowerCase();
    const nameLower = name.toLowerCase();
    return Array.from(hiddenPaths).some(hidden => {
      const hiddenLower = hidden.toLowerCase();
      return pathLower.includes(hiddenLower) || nameLower === hiddenLower;
    });
  }, [hiddenPaths]);
  
  // Scan folder for extensions, stats, and build file tree
  const scanFolderForExtensions = useCallback(async (dirHandle: FileSystemDirectoryHandle, recursive: boolean): Promise<{
    extensions: Set<string>;
    stats: Record<string, { count: number; totalSize: number }>;
    tree: any[];
  }> => {
    const extensions = new Set<string>();
    const stats: Record<string, { count: number; totalSize: number }> = {};
    const tree: any[] = [];
    
    const scanDirectory = async (
      handle: FileSystemDirectoryHandle, 
      parentPath: string = '',
      depth: number = 0
    ): Promise<any[]> => {
      if (depth > 20) return [];
      
      const children: any[] = [];
      
      try {
        // @ts-ignore - FileSystemDirectoryHandle.entries() is valid but TypeScript types may be incomplete
        for await (const [name, entry] of handle.entries()) {
          const fullPath = parentPath ? `${parentPath}/${name}` : name;
          
          if (shouldIgnore(fullPath, name)) {
            continue;
          }
          
          if (entry.kind === 'file') {
            const fileHandle = entry as FileSystemFileHandle;
            const fileName = name;
            
            let fileSize = 0;
            try {
              const file = await fileHandle.getFile();
              fileSize = file.size;
            } catch (e) {
              // Size not available
            }
            
            const lastDot = fileName.lastIndexOf('.');
            let ext = '';
            if (lastDot > 0 && lastDot < fileName.length - 1) {
              ext = fileName.substring(lastDot).toLowerCase();
              extensions.add(ext);
              
              if (!stats[ext]) {
                stats[ext] = { count: 0, totalSize: 0 };
              }
              stats[ext].count++;
              stats[ext].totalSize += fileSize;
            } else {
              ext = '(no extension)';
              if (!stats[ext]) {
                stats[ext] = { count: 0, totalSize: 0 };
              }
              stats[ext].count++;
              stats[ext].totalSize += fileSize;
            }
            
            children.push({
              name: fileName,
              path: fullPath,
              type: 'file',
              extension: ext,
              size: fileSize,
              fileHandle, // Store for later CSV reading
            });
            
          } else if (entry.kind === 'directory' && recursive) {
            const dirChildren = await scanDirectory(entry as FileSystemDirectoryHandle, fullPath, depth + 1);
            children.push({
              name: name,
              path: fullPath,
              type: 'directory',
              children: dirChildren,
            });
          }
        }
      } catch (error) {
        console.error('Error scanning directory:', error);
      }
      
      return children;
    };
    
    const rootChildren = await scanDirectory(dirHandle);
    tree.push({
      name: dirHandle.name,
      path: '',
      type: 'directory',
      children: rootChildren,
    });
    
    return { extensions, stats, tree };
  }, [shouldIgnore]);
  
  // Check if path is a UNC path (network share)
  const isUncPath = useCallback((path: string): boolean => {
    return path.startsWith('\\\\') || path.startsWith('//');
  }, []);
  
  // Check if path is an SSH path
  const isSshPath = useCallback((path: string): boolean => {
    return path.startsWith('ssh://') || path.startsWith('sftp://') || path.includes('@') && path.includes(':');
  }, []);
  
  // Test SSH connection
  const testSshConnection = useCallback(async () => {
    if (!sshHost || !sshUser || (!sshPassword && !sshKeyPath)) {
      setError('SSH host, user, and either password or key path are required');
      return false;
    }
    
    setTestingConnection(true);
    setError(null);
    
    try {
      // TODO: Implement actual SSH connection test via API
      // For now, just validate the connection parameters
      const connectionString = `ssh://${sshUser}@${sshHost}:${sshPort}${sshRemotePath ? '/' + sshRemotePath : ''}`;
      setFolderPath(connectionString);
      setTestingConnection(false);
      return true;
    } catch (error: any) {
      setTestingConnection(false);
      setError(`SSH connection test failed: ${error.message}`);
      return false;
    }
  }, [sshHost, sshPort, sshUser, sshPassword, sshKeyPath, sshRemotePath]);

  // Handle folder selection
  const handleSelectFolder = useCallback(async () => {
    try {
      if (!('showDirectoryPicker' in window)) {
        alert('Folder picker is not supported in this browser. Please use Chrome, Edge, or Opera.');
        return;
      }
      
      setScanningFolder(true);
      
      const dirHandle = await (window as any).showDirectoryPicker();
      const folderName = dirHandle.name;
      let fullPath = folderName;
      
      try {
        if ((dirHandle as any).path) {
          fullPath = (dirHandle as any).path;
        }
      } catch (e) {
        // Path not available
      }
      
      // Check if UNC path (browsers can't access UNC paths directly)
      if (isUncPath(fullPath)) {
        setScanningFolder(false);
        alert(
          'UNC paths (network shares like \\\\server\\share) cannot be accessed directly from the browser.\n\n' +
          'Options:\n' +
          '1. Map the network drive first (e.g., Z:), then select the mapped drive\n' +
          '2. Use the PowerShell script (explore-keystone-folder-structure.ps1) to scan network shares\n' +
          '3. Use a server-side integration service to access network shares'
        );
        return;
      }
      
      if (!folderPath) {
        setFolderPath(fullPath);
      }
      
      setIsNameAutoGenerated(true);
      
      const { extensions, stats, tree } = await scanFolderForExtensions(dirHandle, true);
      const extensionsArray = Array.from(extensions).sort();
      setSelectedExtensions(extensionsArray);
      setExtensionStats(stats);
      setFileTree(tree);
      setShowFileTree(true);
      setDirectoryHandle(dirHandle);
      
      setScanningFolder(false);
    } catch (error: any) {
      setScanningFolder(false);
      if (error.name !== 'AbortError') {
        console.error('Error selecting folder:', error);
        alert(`Failed to select folder: ${error.message}`);
      }
    }
  }, [scanFolderForExtensions, folderPath, isUncPath]);
  
  // Recalculate extension stats when hidden paths or selected extensions change
  useEffect(() => {
    if (fileTree.length === 0) return;
    
    const allStats: Record<string, { count: number; totalSize: number }> = {};
    const countAllFiles = (node: any): void => {
      if (shouldIgnore(node.path || '', node.name)) return;
      if (node.type === 'file') {
        const ext = node.extension || '(no extension)';
        if (!allStats[ext]) {
          allStats[ext] = { count: 0, totalSize: 0 };
        }
        allStats[ext].count++;
        allStats[ext].totalSize += node.size || 0;
      } else if (node.children) {
        node.children.forEach(countAllFiles);
      }
    };
    fileTree.forEach(countAllFiles);
    setExtensionStats(allStats);
  }, [fileTree, hiddenPaths, shouldIgnore]);
  
  // Calculate files that will be uploaded
  const calculateUploadFiles = useCallback(() => {
    if (fileTree.length === 0) return { files: [], totalSize: 0, byExtension: {}, filteredTree: [] };
    
    const files: Array<{ name: string; size: number; extension: string; path: string; rows?: number; columns?: number }> = [];
    const byExtension: Record<string, { count: number; totalSize: number }> = {};
    let totalSize = 0;
    const selectedFilesSet = new Set<string>();
    
    const collectFiles = (node: any) => {
      if (shouldIgnore(node.path || '', node.name)) return;
      
      if (node.type === 'file') {
        const ext = node.extension || '(no extension)';
        if (selectedExtensions.includes(ext) && supportedExtensions.has(ext)) {
          const filePath = node.path || node.name;
          selectedFilesSet.add(filePath);
          
          const metadata = fileMetadata[filePath] || {};
          
          files.push({ 
            name: node.name, 
            size: node.size || 0, 
            extension: ext,
            path: filePath,
            rows: metadata.rows,
            columns: metadata.columns
          });
          totalSize += node.size || 0;
          
          if (!byExtension[ext]) {
            byExtension[ext] = { count: 0, totalSize: 0 };
          }
          byExtension[ext].count++;
          byExtension[ext].totalSize += node.size || 0;
        }
      } else if (node.children) {
        node.children.forEach(collectFiles);
      }
    };
    
    fileTree.forEach(collectFiles);
    files.sort((a, b) => a.name.localeCompare(b.name));
    
    // Build filtered tree
    const buildFilteredFileTree = (tree: any[], selectedFiles: Set<string>): any[] => {
      const filterTree = (node: any): any | null => {
        if (node.type === 'file') {
          const ext = node.extension || '(no extension)';
          if (selectedExtensions.includes(ext) && supportedExtensions.has(ext)) {
            return { ...node, isSelected: true };
          }
          return null;
        } else if (node.children) {
          const filteredChildren = node.children
            .map(filterTree)
            .filter((child: any) => child !== null);
          
          if (filteredChildren.length > 0) {
            return {
              ...node,
              children: filteredChildren,
              hasSelectedFiles: true
            };
          }
          return null;
        }
        return null;
      };
      
      return tree.map(filterTree).filter((node: any) => node !== null);
    };
    
    const filteredTree = buildFilteredFileTree(fileTree, selectedFilesSet);
    
    return { files, totalSize, byExtension, filteredTree };
  }, [fileTree, selectedExtensions, supportedExtensions, shouldIgnore, fileMetadata]);
  
  // Estimate job duration
  const estimateJobDuration = useCallback((byExtension: Record<string, { count: number; totalSize: number }>) => {
    const estimates: Record<string, number> = {
      '.pdf': 2,
      '.docx': 2,
      '.doc': 2,
      '.pptx': 2,
      '.ppt': 2,
      '.xlsx': 1,
      '.xls': 1,
      '.csv': 0.5,
      '.jpg': 5,
      '.jpeg': 5,
      '.png': 5,
      '.gif': 5,
      '.bmp': 5,
      '.tiff': 5,
      '.tif': 5,
      '.dwg': 3,
      '.dxf': 3,
    };
    
    let totalSeconds = 0;
    Object.entries(byExtension).forEach(([ext, data]) => {
      const secondsPerMB = estimates[ext] || 2;
      const sizeMB = data.totalSize / (1024 * 1024);
      totalSeconds += sizeMB * secondsPerMB;
      totalSeconds += data.count * 0.5;
    });
    
    const minutes = Math.ceil(totalSeconds / 60);
    if (minutes < 1) return 'Less than 1 minute';
    if (minutes < 60) return `~${minutes} minute${minutes > 1 ? 's' : ''}`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  }, []);
  
  // Expand all paths in modal when it opens
  useEffect(() => {
    if (!showConfirmModal || fileTree.length === 0) return;
    
    const uploadInfo = calculateUploadFiles();
    const getAllPaths = (n: any, paths: Set<string> = new Set()): Set<string> => {
      const nodePath = n.path || n.name;
      const nodeName = n.name;
      if (nodePath) paths.add(nodePath);
      if (nodeName && nodeName !== nodePath) paths.add(nodeName);
      if (n.children && Array.isArray(n.children)) {
        n.children.forEach((child: any) => getAllPaths(child, paths));
      }
      return paths;
    };
    const allPaths = new Set<string>();
    uploadInfo.filteredTree.forEach((n: any) => getAllPaths(n, allPaths));
    if (uploadInfo.filteredTree.length > 0 && uploadInfo.filteredTree[0].name) {
      allPaths.add(uploadInfo.filteredTree[0].name);
    }
    setModalExpandedPaths(allPaths);
  }, [showConfirmModal, fileTree, calculateUploadFiles]);
  
  // Extract file metadata for CSV files
  useEffect(() => {
    if (!showConfirmModal || !directoryHandle || fileTree.length === 0) return;
    
    const readFileMetadata = async () => {
      const metadata: Record<string, { rows?: number; columns?: number }> = {};
      const deterministicFiles: Array<{ path: string; extension: string }> = [];
      
      const collectFiles = (node: any) => {
        if (shouldIgnore(node.path || '', node.name)) return;
        if (node.type === 'file') {
          const ext = node.extension || '';
          if (['.csv'].includes(ext) &&
              selectedExtensions.includes(ext) && 
              supportedExtensions.has(ext)) {
            deterministicFiles.push({
              path: node.path || node.name,
              extension: ext
            });
          }
        } else if (node.children) {
          node.children.forEach(collectFiles);
        }
      };
      
      fileTree.forEach(collectFiles);
      
      for (const fileInfo of deterministicFiles) {
        if (fileInfo.extension === '.csv') {
          try {
            const pathParts = fileInfo.path.split('/').filter(p => p);
            let currentHandle: FileSystemDirectoryHandle | FileSystemFileHandle = directoryHandle;
            
            for (let i = 0; i < pathParts.length - 1; i++) {
              currentHandle = await (currentHandle as FileSystemDirectoryHandle).getDirectoryHandle(pathParts[i]);
            }
            
            const fileHandle = await (currentHandle as FileSystemDirectoryHandle).getFileHandle(pathParts[pathParts.length - 1]);
            const file = await fileHandle.getFile();
            const text = await file.text();
            const lines = text.split('\n').filter(line => line.trim());
            if (lines.length === 0) {
              metadata[fileInfo.path] = { rows: 0, columns: 0 };
              continue;
            }
            
            const firstLine = lines[0];
            const columns = firstLine.split(',').length;
            const rows = lines.length;
            metadata[fileInfo.path] = { rows, columns };
          } catch (error) {
            console.warn(`Failed to read CSV metadata for ${fileInfo.path}:`, error);
          }
        }
      }
      
      setFileMetadata(metadata);
    };
    
    readFileMetadata();
  }, [showConfirmModal, directoryHandle, fileTree, selectedExtensions, supportedExtensions, shouldIgnore]);
  
  const toggleHidePath = useCallback((path: string, name: string) => {
    setHiddenPaths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path) || newSet.has(name)) {
        newSet.delete(path);
        newSet.delete(name);
      } else {
        newSet.add(path);
        newSet.add(name);
      }
      return newSet;
    });
    setShowFileTree(true);
  }, []);
  
  const selectCategory = useCallback((category: keyof typeof fileExtensionGroups) => {
    const group = fileExtensionGroups[category];
    if (!group) return;
    
    const newSelected = new Set(selectedExtensions);
    group.extensions.forEach(ext => {
      if (supportedExtensions.has(ext)) {
        newSelected.add(ext);
      }
    });
    setSelectedExtensions(Array.from(newSelected));
  }, [selectedExtensions, supportedExtensions]);
  
  const deselectCategory = useCallback((category: keyof typeof fileExtensionGroups) => {
    const group = fileExtensionGroups[category];
    if (!group) return;
    
    const extensionsToRemove = new Set(group.extensions);
    const newSelected = selectedExtensions.filter((ext: string) => !extensionsToRemove.has(ext));
    setSelectedExtensions(newSelected);
  }, [selectedExtensions]);
  
  const handleSubmit = async () => {
    setError(null);
    
    if (!folderName || !folderPath) {
      setError('Name and file path are required');
      return;
    }
    
    const uploadInfo = calculateUploadFiles();
    const getAllPaths = (n: any, paths: Set<string> = new Set()): Set<string> => {
      const nodePath = n.path || n.name;
      if (nodePath) paths.add(nodePath);
      if (n.children && Array.isArray(n.children)) {
        n.children.forEach((child: any) => getAllPaths(child, paths));
      }
      return paths;
    };
    const allPaths = new Set<string>();
    uploadInfo.filteredTree.forEach((n: any) => getAllPaths(n, allPaths));
    if (uploadInfo.filteredTree.length > 0 && uploadInfo.filteredTree[0].name) {
      allPaths.add(uploadInfo.filteredTree[0].name);
    }
    setModalExpandedPaths(allPaths);
    
    setShowConfirmModal(true);
  };
  
  const performSubmit = async () => {
    setError(null);
    setLoading(true);
    
    try {
      if (!folderName || !folderPath) {
        setError('Name and file path are required');
        setLoading(false);
        return;
      }
      
      // Build exclude patterns from hidden paths
      const excludePatterns = Array.from(hiddenPaths).map(path => {
        if (path.includes('*')) return path;
        return `**/${path}/**`;
      });
      
      const supportedSelectedExtensions = selectedExtensions.filter(ext => 
        supportedExtensions.has(ext)
      );
      
      // Get file statistics from calculateUploadFiles
      const uploadInfo = calculateUploadFiles();
      
      // Build files array for API (only include files that will be uploaded)
      const filesForAPI = uploadInfo.files.map(file => ({
        name: file.name,
        path: file.path,
        size: file.size,
        extension: file.extension,
        rows: file.rows,
        columns: file.columns,
      }));
      
      // Build files_by_type from byExtension
      const filesByType: Record<string, number> = {};
      Object.entries(uploadInfo.byExtension).forEach(([ext, data]) => {
        filesByType[ext] = data.count;
      });
      
      // Build connection config for SSH/remote connections
      const connectionConfig = connectionType === 'ssh' ? {
        type: 'ssh',
        host: sshHost,
        port: parseInt(sshPort) || 22,
        username: sshUser,
        password: sshPassword || undefined,
        key_path: sshKeyPath || undefined,
        remote_path: sshRemotePath,
      } : connectionType === 'unc' ? {
        type: 'unc',
        unc_path: folderPath,
      } : undefined;
      
      const response = await registerLocalFolderSource({
        project_id: projectId,
        name: folderName,
        file_path: connectionType === 'ssh' ? sshRemotePath : folderPath,
        description: folderDescription || undefined,
        watch_for_changes: watchForChanges,
        recursive: recursive,
        file_extensions: supportedSelectedExtensions.length > 0 ? supportedSelectedExtensions : undefined,
        exclude_patterns: excludePatterns.length > 0 ? excludePatterns : undefined,
        // Include file statistics from frontend scan
        files: filesForAPI.length > 0 ? filesForAPI : undefined,
        file_count: uploadInfo.files.length,
        total_size_bytes: uploadInfo.totalSize,
        files_by_type: Object.keys(filesByType).length > 0 ? filesByType : undefined,
        // Include connection config for SSH/remote
        connection_config: connectionConfig,
      });
      
      console.log('[SOURCE REGISTRATION] ✅ Local folder source registered successfully:', response);
      
      setLoading(false);
      setShowConfirmModal(false);
      
      // For client-side sources, always use artifact upload
      if (response?.id && directoryHandle) {
        console.log('[SOURCE REGISTRATION] Client-side source registered - prompting for artifact upload');
        setUploadSourceId(response.id);
        setShowClientUploadPrompt(true);
        onSuccess();
        onClose();
      } else {
        onSuccess();
        onClose();
      }
      
    } catch (err: any) {
      console.error('[SOURCE REGISTRATION] ❌ Error registering source:', err);
      setLoading(false);
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message || 
                          err.message || 
                          'Failed to register source';
      setError(errorMessage);
    }
  };
  
  return (
    <>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Connection Type *
          </label>
          <div className="flex gap-2 mb-3">
            <Button
              type="button"
              variant={connectionType === 'local' ? 'default' : 'outline'}
              onClick={() => setConnectionType('local')}
              size="sm"
            >
              Local Folder
            </Button>
            {isKeystoneTenant && (
              <>
                <Button
                  type="button"
                  variant={connectionType === 'ssh' ? 'default' : 'outline'}
                  onClick={() => setConnectionType('ssh')}
                  size="sm"
                >
                  SSH/SFTP (On-Prem)
                </Button>
                <Button
                  type="button"
                  variant={connectionType === 'unc' ? 'default' : 'outline'}
                  onClick={() => setConnectionType('unc')}
                  size="sm"
                >
                  Network Share (UNC)
                </Button>
              </>
            )}
          </div>
          {!isKeystoneTenant && (
            <p className="text-xs text-gray-500 mb-2">
              SSH/SFTP and UNC connections are only available for Keystone tenant projects.
            </p>
          )}
          
          {connectionType === 'local' && (
            <>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Folder Path *
              </label>
              <div className="flex gap-2">
                <Input
                  value={folderPath}
                  onChange={(e) => setFolderPath(e.target.value)}
                  placeholder="e.g., /data/excel/ or C:\\Documents\\Reports"
                  className="flex-1"
                />
                <Button
                  onClick={handleSelectFolder}
                  disabled={scanningFolder}
                  variant="outline"
                >
                  {scanningFolder ? 'Scanning...' : 'Choose Folder'}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                The folder path must exist and be accessible on the server.
              </p>
            </>
          )}
          
          {connectionType === 'ssh' && (
            <div className="space-y-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SSH Host *
                  </label>
                  <Input
                    value={sshHost}
                    onChange={(e) => setSshHost(e.target.value)}
                    placeholder="e.g., server.example.com or 192.168.1.100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SSH Port
                  </label>
                  <Input
                    value={sshPort}
                    onChange={(e) => setSshPort(e.target.value)}
                    placeholder="22"
                    type="number"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SSH Username *
                </label>
                <Input
                  value={sshUser}
                  onChange={(e) => setSshUser(e.target.value)}
                  placeholder="e.g., username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SSH Password
                </label>
                <Input
                  value={sshPassword}
                  onChange={(e) => setSshPassword(e.target.value)}
                  type="password"
                  placeholder="Leave empty if using SSH key"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SSH Key Path (Alternative to Password)
                </label>
                <Input
                  value={sshKeyPath}
                  onChange={(e) => setSshKeyPath(e.target.value)}
                  placeholder="e.g., /path/to/private_key or ~/.ssh/id_rsa"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Remote Folder Path *
                </label>
                <Input
                  value={sshRemotePath}
                  onChange={(e) => setSshRemotePath(e.target.value)}
                  placeholder="e.g., /data/documents or /mnt/shared/folder"
                />
              </div>
              <Button
                onClick={testSshConnection}
                disabled={testingConnection || !sshHost || !sshUser}
                variant="outline"
                className="w-full"
              >
                {testingConnection ? 'Testing Connection...' : 'Test SSH Connection'}
              </Button>
              <p className="text-xs text-gray-500 mt-1">
                SSH credentials will be encrypted and stored securely. Connection will be tested server-side.
              </p>
            </div>
          )}
          
          {connectionType === 'unc' && (
            <>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Network Share Path (UNC) *
              </label>
              <Input
                value={folderPath}
                onChange={(e) => setFolderPath(e.target.value)}
                placeholder="e.g., \\\\server\\share\\folder"
                className="flex-1"
              />
              <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <p className="text-xs text-yellow-800 dark:text-yellow-200 font-semibold mb-1">
                  ⚠️ Network Share (UNC Path)
                </p>
                <p className="text-xs text-yellow-700 dark:text-yellow-300">
                  UNC paths require server-side access. The server must have access to the network share.
                </p>
                <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                  <strong>Requirements:</strong>
                </p>
                <ul className="text-xs text-yellow-700 dark:text-yellow-300 mt-1 ml-4 list-disc">
                  <li>Server must have network access to the share</li>
                  <li>Service account must have read permissions</li>
                  <li>Share must be accessible from the TraceOS server</li>
                </ul>
              </div>
            </>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Source Name *
          </label>
          <Input
            value={folderName}
            onChange={(e) => {
              setFolderName(e.target.value);
              setIsNameAutoGenerated(false);
            }}
            onFocus={() => {
              if (!folderName && autoGeneratedName) {
                setFolderName(autoGeneratedName);
                setIsNameAutoGenerated(true);
              }
            }}
            placeholder={autoGeneratedName || "e.g., Excel Data Files, PDF Reports"}
          />
          {isNameAutoGenerated && autoGeneratedName && (
            <p className="text-xs text-gray-500 mt-1">
              Auto-generated from folder and file selection
            </p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description (Optional)
          </label>
          <Input
            value={folderDescription}
            onChange={(e) => setFolderDescription(e.target.value)}
            placeholder="Brief description of this source"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="watchForChanges"
              checked={watchForChanges}
              onChange={(e) => setWatchForChanges(e.target.checked)}
              className="rounded border-gray-300"
            />
            <label htmlFor="watchForChanges" className="text-sm text-gray-700">
              Watch for changes
            </label>
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="recursive"
              checked={recursive}
              onChange={(e) => setRecursive(e.target.checked)}
              className="rounded border-gray-300"
            />
            <label htmlFor="recursive" className="text-sm text-gray-700">
              Include subdirectories
            </label>
          </div>
        </div>
        
        {/* File Extension Selection */}
        {Object.keys(extensionStats).length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              File Extensions ({selectedExtensions.length} selected)
            </label>
            
            {Object.entries(fileExtensionGroups).map(([categoryKey, group]) => {
              if (categoryKey === 'other') return null;
              
              const detectedExtensions = group.extensions.filter(ext => extensionStats[ext]);
              if (detectedExtensions.length === 0) return null;
              
              const allSelected = detectedExtensions.every(ext => selectedExtensions.includes(ext));
              const someSelected = detectedExtensions.some(ext => selectedExtensions.includes(ext));
              
              return (
                <div key={categoryKey} className="mb-4 p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{group.label}</h4>
                      <p className="text-xs text-gray-500">{group.description}</p>
                    </div>
                    {detectedExtensions.length > 0 && (
                      <div className="flex gap-2">
                        {!allSelected && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => selectCategory(categoryKey as keyof typeof fileExtensionGroups)}
                            className="text-xs"
                          >
                            Select All
                          </Button>
                        )}
                        {someSelected && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deselectCategory(categoryKey as keyof typeof fileExtensionGroups)}
                            className="text-xs"
                          >
                            Deselect All
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {detectedExtensions.map(ext => {
                      const isSelected = selectedExtensions.includes(ext);
                      const stats = extensionStats[ext] || { count: 0, totalSize: 0 };
                      
                      return (
                        <button
                          key={ext}
                          type="button"
                          onClick={() => {
                            if (isSelected) {
                              setSelectedExtensions(prev => prev.filter(e => e !== ext));
                            } else {
                              setSelectedExtensions(prev => [...prev, ext]);
                            }
                          }}
                          className="cursor-pointer hover:opacity-80 transition-opacity"
                        >
                          <Badge
                            variant={isSelected ? 'success' : 'default'}
                            className="min-h-[32px] flex items-center gap-1"
                          >
                          <span>{ext}</span>
                          <span className="text-xs opacity-75">
                            ({stats.count} files, {formatFileSize(stats.totalSize)})
                          </span>
                          {isSelected && (
                            <X 
                              className="h-3 w-3 ml-1 hover:bg-red-200 rounded" 
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedExtensions(prev => prev.filter(e => e !== ext));
                              }}
                            />
                          )}
                        </Badge>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* File Tree */}
        {showFileTree && fileTree.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                File Tree
                {(() => {
                  const uploadInfo = calculateUploadFiles();
                  const totalFiles = uploadInfo.files.length;
                  const allFiles = fileTree.reduce((count, node) => {
                    const countFiles = (n: any): number => {
                      if (n.type === 'file') return 1;
                      if (n.children) return n.children.reduce((sum: number, child: any) => sum + countFiles(child), 0);
                      return 0;
                    };
                    return count + countFiles(node);
                  }, 0);
                  return (
                    <span className="text-xs text-gray-500 ml-2">
                      ({totalFiles} files included / {allFiles} total)
                    </span>
                  );
                })()}
              </label>
              <button
                onClick={() => setShowFileTree(!showFileTree)}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {showFileTree ? 'Hide' : 'Show'} Tree
              </button>
            </div>
            
            {showFileTree && (
              <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto p-3 bg-gray-50">
                {fileTree.map((node: any, idx: number) => (
                  <FileTreeNode
                    key={idx}
                    node={node}
                    level={0}
                    toggleHidePath={toggleHidePath}
                    hiddenPaths={hiddenPaths}
                    formatFileSize={formatFileSize}
                    supportedExtensions={supportedExtensions}
                    extensionDescriptions={extensionDescriptions}
                    selectedExtensions={selectedExtensions}
                    expandedTreePaths={expandedTreePaths}
                    setExpandedTreePaths={setExpandedTreePaths}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
        
        <div className="flex gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading || !folderName || !folderPath}>
            {loading ? 'Registering...' : 'Register Source'}
          </Button>
        </div>
      </div>
      
      {/* Confirmation Modal */}
      {showConfirmModal && (
        <ConfirmRegistrationModal
          uploadInfo={calculateUploadFiles()}
          duration={estimateJobDuration(calculateUploadFiles().byExtension)}
          onConfirm={performSubmit}
          onCancel={() => setShowConfirmModal(false)}
          loading={loading}
          confirmModalTab={confirmModalTab}
          setConfirmModalTab={setConfirmModalTab}
          modalExpandedPaths={modalExpandedPaths}
          setModalExpandedPaths={setModalExpandedPaths}
          formatFileSize={formatFileSize}
          supportedExtensions={supportedExtensions}
          extensionDescriptions={extensionDescriptions}
          selectedExtensions={selectedExtensions}
        />
      )}
      
      {/* Client Upload Prompt Modal */}
      {showClientUploadPrompt && uploadSourceId && directoryHandle && (
        <ClientUploadPromptModal
          projectId={projectId}
          sourceId={uploadSourceId}
          directoryHandle={directoryHandle}
          uploadInfo={calculateUploadFiles()}
          selectedExtensions={selectedExtensions}
          supportedExtensions={supportedExtensions}
          shouldIgnore={shouldIgnore}
          onClose={() => {
            setShowClientUploadPrompt(false);
            onSuccess();
            onClose();
          }}
        />
      )}
    </>
  );
}

